import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  Box,
  Grid,
  Paper,
  Text,
  Button,
  Group,
  Badge,
  ActionIcon,
  Menu,
  TextInput,
  LoadingOverlay,
  Image,
  Card,
  Center,
  Stack,
  ThemeIcon,
} from "@mantine/core";
import {
  IconPlus,
  IconDotsVertical,
  IconEdit,
  IconTrash,
  IconEye,
  IconSearch,
  IconBrain,
  IconQuestionMark,
  IconUsers,
} from "@tabler/icons-react";
import { API_BASE_URL } from "../../config/baseUrls";

export default function QuizManagement() {
  const [quizzes, setQuizzes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const navigate = useNavigate();

  // --- LOGIC (UNTOUCHED) ---
  const fetchQuizzes = async () => {
    try {
      const token = localStorage.getItem("adminToken");
      if (!token) {
        navigate("/admin");
        return;
      }
      const response = await fetch(
        `${API_BASE_URL}/admin/quizzes`,
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        },
      );
      const result = await response.json();
      if (result.success) {
        setQuizzes(result.data);
      } else {
        setError("Failed to fetch quizzes");
      }
    } catch (err) {
      console.error("Fetch quizzes error:", err);
      setError("Network error. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteQuiz = async (quizId, quizTitle) => {
    if (!window.confirm(`Are you sure you want to delete "${quizTitle}"?`))
      return;
    try {
      const token = localStorage.getItem("adminToken");
      const response = await fetch(
        `${API_BASE_URL}/admin/quizzes/${quizId}`,
        {
          method: "DELETE",
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      const result = await response.json();
      if (result.success) {
        setSuccess("Quiz deleted successfully!");
        setQuizzes(quizzes.filter((quiz) => quiz.quizId !== quizId));
        setTimeout(() => setSuccess(""), 3000);
      } else {
        setError("Failed to delete quiz");
      }
    } catch (err) {
      console.error("Delete quiz error:", err);
      setError("Network error. Please try again.");
    }
  };

  useEffect(() => {
    fetchQuizzes();
  }, []);

  const filteredQuizzes = quizzes.filter(
    (quiz) =>
      quiz.titleEn.toLowerCase().includes(searchTerm.toLowerCase()) ||
      quiz.titleJa.toLowerCase().includes(searchTerm.toLowerCase()),
  );
  // --- LOGIC ENDS ---

  return (
    <Box>
      {/* Header & Actions */}
      <Group justify="space-between" mb="lg">
        <Box>
          <Text size="xl" fw={800} className="ota-gradient-text">
            Quiz Library
          </Text>
          <Text size="sm" c="dimmed">
            Manage standard 5-question daily quizzes
          </Text>
        </Box>
        <Button
          leftSection={<IconPlus size={18} />}
          className="btn-ota-primary"
          onClick={() => navigate("/admin/dashboard/quiz-management/new")}
        >
          Create Quiz
        </Button>
      </Group>

      {/* Stats Overview */}
      <Grid mb="xl">
        <Grid.Col span={{ base: 12, md: 4 }}>
          <Paper
            withBorder
            p="md"
            radius="md"
            className="flex items-center space-x-4"
          >
            <ThemeIcon size={48} radius="md" variant="light" color="blue">
              <IconBrain size={24} />
            </ThemeIcon>
            <div>
              <Text c="dimmed" size="xs" tt="uppercase" fw={700}>
                Total Quizzes
              </Text>
              <Text fw={700} size="xl">
                {quizzes.length}
              </Text>
            </div>
          </Paper>
        </Grid.Col>
        <Grid.Col span={{ base: 12, md: 4 }}>
          <Paper
            withBorder
            p="md"
            radius="md"
            className="flex items-center space-x-4"
          >
            <ThemeIcon size={48} radius="md" variant="light" color="green">
              <IconQuestionMark size={24} />
            </ThemeIcon>
            <div>
              <Text c="dimmed" size="xs" tt="uppercase" fw={700}>
                Total Questions
              </Text>
              <Text fw={700} size="xl">
                {quizzes.length * 5}
              </Text>
            </div>
          </Paper>
        </Grid.Col>
        <Grid.Col span={{ base: 12, md: 4 }}>
          <Paper
            withBorder
            p="md"
            radius="md"
            className="flex items-center space-x-4"
          >
            <ThemeIcon size={48} radius="md" variant="light" color="purple">
              <IconUsers size={24} />
            </ThemeIcon>
            <div>
              <Text c="dimmed" size="xs" tt="uppercase" fw={700}>
                Participants
              </Text>
              <Text fw={700} size="xl">
                {quizzes.reduce(
                  (sum, quiz) => sum + (quiz._count?.ranking || 0),
                  0,
                )}
              </Text>
            </div>
          </Paper>
        </Grid.Col>
      </Grid>

      {/* Search */}
      <TextInput
        placeholder="Search by title (English or Japanese)..."
        leftSection={<IconSearch size={16} />}
        mb="lg"
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        size="md"
        radius="md"
      />

      {/* Loading State */}
      {loading ? (
        <Center h={200}>
          <Stack align="center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
            <Text c="dimmed">Loading...</Text>
          </Stack>
        </Center>
      ) : filteredQuizzes.length === 0 ? (
        <Paper p="xl" withBorder radius="md" ta="center" bg="gray.0">
          <ThemeIcon size={60} radius="xl" color="gray" variant="light" mb="md">
            <IconBrain size={30} />
          </ThemeIcon>
          <Text fw={600} size="lg">
            No Quizzes Found
          </Text>
          <Text c="dimmed" size="sm" mb="md">
            Try adjusting your search or create a new one.
          </Text>
        </Paper>
      ) : (
        /* Quiz Grid */
        <Grid>
          {filteredQuizzes.map((quiz) => (
            <Grid.Col key={quiz.quizId} span={{ base: 12, sm: 6, lg: 4 }}>
              <Card
                withBorder
                padding="0"
                radius="md"
                className="hover:shadow-md transition-shadow"
              >
                <Card.Section>
                  <Box h={160} bg="gray.1" pos="relative">
                    {quiz.coverImg ? (
                      <Image
                        src={quiz.coverImg}
                        h={160}
                        fit="cover"
                        alt="Quiz Cover"
                      />
                    ) : (
                      <Center h="100%">
                        <IconBrain size={40} color="gray" />
                      </Center>
                    )}
                    <Badge
                      className="absolute top-2 right-2 shadow-sm"
                      color="dark"
                    >
                      {quiz._count?.question || 5} Qs
                    </Badge>
                  </Box>
                </Card.Section>

                <Stack p="md" gap="xs">
                  <Text fw={600} size="lg" lineClamp={1}>
                    {quiz.titleEn}
                  </Text>
                  <Text c="dimmed" size="sm" lineClamp={1}>
                    {quiz.titleJa}
                  </Text>

                  <Group justify="space-between" mt="md">
                    <Group gap="xs">
                      <IconUsers size={14} className="text-gray-400" />
                      <Text size="xs" c="dimmed">
                        {quiz._count?.ranking || 0} Played
                      </Text>
                    </Group>

                    <Group gap={4}>
                      <ActionIcon
                        variant="light"
                        color="gray"
                        onClick={() =>
                          navigate(
                            `/admin/dashboard/quiz-management/preview/${quiz.quizId}`,
                          )
                        }
                      >
                        <IconEye size={16} />
                      </ActionIcon>
                      <ActionIcon
                        variant="light"
                        color="blue"
                        onClick={() =>
                          navigate(
                            `/admin/dashboard/quiz-management/edit/${quiz.quizId}`,
                          )
                        }
                      >
                        <IconEdit size={16} />
                      </ActionIcon>
                      <Menu position="bottom-end">
                        <Menu.Target>
                          <ActionIcon variant="light" color="red">
                            <IconTrash size={16} />
                          </ActionIcon>
                        </Menu.Target>
                        <Menu.Dropdown>
                          <Menu.Label>Danger Zone</Menu.Label>
                          <Menu.Item
                            color="red"
                            leftSection={<IconTrash size={14} />}
                            onClick={() =>
                              handleDeleteQuiz(quiz.quizId, quiz.titleEn)
                            }
                          >
                            Delete Quiz
                          </Menu.Item>
                        </Menu.Dropdown>
                      </Menu>
                    </Group>
                  </Group>
                </Stack>
              </Card>
            </Grid.Col>
          ))}
        </Grid>
      )}
    </Box>
  );
}
